import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "wouter";
import { Avatar, PillTag } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { UpsellCard } from "@/components/mnf/TierLock";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  tierAtLeast,
  type ChatMessage,
  type GeneratedSession,
  type MovementProfile,
} from "@/lib/store";

function buildSuggestions(
  session: GeneratedSession | null,
  profile: MovementProfile | null,
): string[] {
  const out: string[] = [];
  if (session) {
    const compound = session.exercises.find((e) => e.tier === "compound");
    if (compound) {
      out.push(`Form check on my ${compound.name.toLowerCase()} today?`);
    }
    out.push(`How should ${session.focus.toLowerCase()} feel in week ${session.mesocycleWeek}?`);
  }
  const activeInjuries = (profile?.injuries ?? []).filter((i) => !i.paused);
  const pelvic = activeInjuries.find((i) => i.flag === "pelvic-floor");
  const joints = activeInjuries.find((i) => i.flag === "joints");
  const spine = activeInjuries.find((i) => i.flag === "spine");
  if (pelvic) out.push("Pelvic floor cue for today's lifts?");
  else if (joints) out.push("Knee-friendly swap for today?");
  else if (spine) out.push("Low-back cue for today's session?");
  if (!session && profile?.completed) {
    out.push("What should I do on a rest day?");
  }
  if (!profile || !profile.completed) {
    out.push("Help me set up my training plan");
    out.push("What does a NASM 4-week block look like?");
  }
  out.push("A verse for tonight, please");
  return out.slice(0, 4);
}

const KILEY_REPLIES = [
  {
    body: "Sister, I hear you. Let's slow it down together. I'll send a 3-day rhythm tonight that protects your sleep and your evenings.",
    verseRef: "Psalm 23:2 \u00b7 'He makes me lie down in green pastures, He leads me beside quiet waters.'",
  },
  {
    body: "I'm sitting with this with you. Take a breath. The Lord is not in a hurry, and neither am I. Tell me what's heaviest right now.",
    verseRef: "Isaiah 41:10 \u00b7 'Do not fear, for I am with you. I will strengthen you and help you.'",
  },
  {
    body: "Yes, that's a beautiful step. I'm proud of you. Let's add a small celebration into this week, not just discipline. Joy is fuel.",
    verseRef: "Nehemiah 8:10 \u00b7 'The joy of the Lord is your strength.'",
  },
  {
    body: "Tend yourself like you'd tend a young plant, gently and consistently. I'll record a 5-minute voice note tonight to walk you through it.",
    verseRef: "Galatians 6:9 \u00b7 'Let us not become weary in doing good, for at the proper time we will reap a harvest.'",
  },
];

function pickReply(seed: number) {
  return KILEY_REPLIES[seed % KILEY_REPLIES.length];
}

function formatStamp(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now); yest.setDate(now.getDate() - 1);
  const isYest = d.toDateString() === yest.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay) return time;
  if (isYest) return `Yesterday \u00b7 ${time}`;
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) + ` \u00b7 ${time}`;
}

export default function KileyChat() {
  const [, setLocation] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  const messages = useStoreQuery(useMemo(() => () => queries.chatThread("kiley"), []));
  const send = useStoreMutation(mutations.sendChatMessage);
  const todaysSession = useStoreQuery(() =>
    me?.id ? queries.todaysSession(me.id, new Date().toISOString()) : null,
  );
  const movementProfile = useStoreQuery(() => (me?.id ? queries.movementProfile(me.id) : null));

  const [draft, setDraft] = useState("");
  const [coachTyping, setCoachTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, coachTyping]);

  const unlocked = tierAtLeast(me?.tier, "Flourishing");

  if (!unlocked) {
    return (
      <div
        className="min-h-full p-5 flex flex-col"
        style={{ background: "var(--mnf-cr)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setLocation("/flourish")}
            className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(30,74,53,0.08)", color: "var(--mnf-f)", border: "none", cursor: "pointer" }}
            data-testid="button-back-locked"
            aria-label="Back"
          >
            <Icon name="arrow_back" size={18} />
          </button>
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>Inner circle</p>
            <p className="mnf-serif italic" style={{ fontSize: 18, color: "var(--mnf-fd)" }}>Direct line to Coach</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4">
          <UpsellCard
            tier="Flourishing"
            eyebrow="Flourishing only"
            title="A direct thread with Coach Kiley"
            description="One-to-one messages and voice replies, anchored in Scripture. Same week response. Move to Flourishing ($197/mo) to open the line."
            ctaLabel="See Flourishing"
            icon="forum"
          />

          <div
            className="mnf-organic p-4"
            style={{
              background: "rgba(245,240,232,0.7)",
              border: "1px solid rgba(30,74,53,0.06)",
            }}
          >
            <p className="mnf-eyebrow mb-2" style={{ color: "var(--mnf-tg)" }}>What sisters say</p>
            <p className="mnf-body italic" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.55 }}>
              "Kiley walked with me through a hard week. Her voice notes felt like a phone call from a friend who actually knew Scripture."
            </p>
            <p className="mnf-body mt-2" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              Mara W. · Flourishing for 8 months
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSend = () => {
    const body = draft.trim();
    if (!body) return;
    send({ threadId: "kiley", fromCoach: false, type: "text", body });
    setDraft("");
    setCoachTyping(true);
    const replyDelay = 1200 + Math.random() * 900;
    setTimeout(() => {
      const reply = pickReply(messages.length);
      send({ threadId: "kiley", fromCoach: true, type: "text", body: reply.body });
      setTimeout(() => {
        send({ threadId: "kiley", fromCoach: true, type: "verse", body: reply.verseRef, verseRef: reply.verseRef });
        setCoachTyping(false);
      }, 700);
    }, replyDelay);
  };

  return (
    <div className="flex flex-col h-full" style={{ background: "var(--mnf-cr)" }}>
      {/* Header */}
      <div
        className="px-4 pt-2 pb-3 flex items-center gap-3 z-10"
        style={{
          background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
          color: "#F5F0E8",
          borderRadius: "0 0 24px 24px",
        }}
      >
        <Link href="/flourish" className="no-underline" data-testid="button-back-kiley">
          <button
            className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(245,240,232,0.12)", color: "#F5F0E8", border: "none", cursor: "pointer" }}
            aria-label="Back"
          >
            <Icon name="arrow_back" size={18} />
          </button>
        </Link>
        <Avatar initials="K" size={42} ringColor="rgba(229,185,151,0.65)" />
        <div className="flex-1 min-w-0">
          <p className="mnf-serif italic" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.1 }}>Coach Kiley</p>
          <div className="flex items-center gap-1.5 mt-1" data-testid="badge-kiley-ggs">
            <span
              className="inline-flex items-center gap-1"
              style={{
                padding: "2px 7px", borderRadius: 999,
                background: "rgba(229,185,151,0.22)",
                border: "1px solid rgba(229,185,151,0.45)",
                color: "#E5B997",
                fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
              }}
            >
              <Icon name="verified" size={9} />
              GGS Pre &amp; Postnatal Certified
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#A8D4B8" }} />
            <span className="mnf-body" style={{ fontSize: 11, color: "rgba(245,240,232,0.7)" }}>
              {coachTyping ? "Typing\u2026" : "Usually replies same week"}
            </span>
          </div>
        </div>
        <PillTag tone="gold">Flourishing</PillTag>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 mnf-scroll">
        <div className="flex justify-center mb-2">
          <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", padding: "4px 12px", background: "rgba(30,74,53,0.06)", borderRadius: 999 }}>
            Direct thread · sealed
          </span>
        </div>
        {messages.map((m) => <KMsgBubble key={m.id} m={m} />)}
        {coachTyping && <TypingBubble />}
      </div>

      {/* Suggestion chips */}
      <SuggestionChips
        session={todaysSession ?? null}
        profile={movementProfile ?? null}
        onPick={(text) => setDraft(text)}
      />

      {/* Composer */}
      <div
        className="px-3 pt-2 pb-4 flex items-end gap-2"
        style={{
          background: "rgba(245,240,232,0.95)",
          backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(30,74,53,0.06)",
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
          placeholder="Ask Coach Kiley anything\u2026"
          data-testid="input-kiley-chat"
          className="flex-1 mnf-body italic outline-none"
          style={{
            padding: "12px 18px", borderRadius: 999,
            background: "rgba(30,74,53,0.05)", color: "var(--mnf-fd)",
            fontSize: 14, border: "1px solid rgba(30,74,53,0.08)",
          }}
        />
        <button
          onClick={handleSend}
          data-testid="button-send-kiley"
          className="flex items-center justify-center shrink-0"
          style={{
            width: 44, height: 44, borderRadius: 999,
            background: "var(--mnf-t)", color: "#fff",
            border: "none", cursor: "pointer",
          }}
          aria-label="Send"
        >
          <Icon name="send" size={18} weight={500} />
        </button>
      </div>
    </div>
  );
}

function KMsgBubble({ m }: { m: ChatMessage }) {
  const fromCoach = m.fromCoach;

  if (m.type === "verse") {
    return (
      <div className="flex justify-start">
        <div
          className="max-w-[85%]"
          style={{
            padding: "10px 14px", borderRadius: 18,
            background: "var(--mnf-purpleL)",
            border: "1px solid rgba(127,119,221,0.25)",
          }}
        >
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)", fontSize: 9 }}>Verse from Kiley</p>
          <p className="mnf-serif italic" style={{ fontSize: 13.5, color: "var(--mnf-fd)", lineHeight: 1.45, marginTop: 4 }}>
            {m.verseRef ?? m.body}
          </p>
        </div>
      </div>
    );
  }

  if (m.type === "voice") {
    return (
      <div className={`flex ${fromCoach ? "justify-start" : "justify-end"}`}>
        <div
          className="flex items-center gap-3"
          style={{
            padding: "10px 14px", borderRadius: 22,
            background: fromCoach ? "#fff" : "var(--mnf-f)",
            color: fromCoach ? "var(--mnf-fd)" : "#F5F0E8",
            border: fromCoach ? "1px solid rgba(30,74,53,0.06)" : "none",
            maxWidth: "85%",
          }}
        >
          <button
            className="flex items-center justify-center shrink-0"
            style={{
              width: 32, height: 32, borderRadius: 999,
              background: fromCoach ? "var(--mnf-purple)" : "rgba(245,240,232,0.18)",
              color: "#fff",
              border: "none", cursor: "pointer",
            }}
            aria-label="Play voice note"
          >
            <Icon name="play_arrow" size={16} weight={600} />
          </button>
          <div>
            <p className="mnf-body" style={{ fontSize: 11, fontWeight: 600, opacity: 0.75 }}>Voice note</p>
            <p className="mnf-body" style={{ fontSize: 12 }}>{m.durationSec ?? 30}s</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${fromCoach ? "justify-start" : "justify-end"}`}>
      <div className="max-w-[85%]">
        <div
          className="mnf-body"
          style={{
            padding: "10px 14px", borderRadius: fromCoach ? "18px 18px 18px 6px" : "18px 18px 6px 18px",
            background: fromCoach ? "#fff" : "var(--mnf-f)",
            color: fromCoach ? "var(--mnf-fd)" : "#F5F0E8",
            border: fromCoach ? "1px solid rgba(30,74,53,0.06)" : "none",
            fontSize: 13.5, lineHeight: 1.5,
          }}
        >
          {m.body}
        </div>
        <p className="mnf-body" style={{ fontSize: 9.5, color: "var(--mnf-tg)", marginTop: 3, textAlign: fromCoach ? "left" : "right", paddingInline: 8 }}>
          {formatStamp(m.sentAt)}
        </p>
      </div>
    </div>
  );
}

function SuggestionChips({
  session,
  profile,
  onPick,
}: {
  session: GeneratedSession | null;
  profile: MovementProfile | null;
  onPick: (text: string) => void;
}) {
  const chips = useMemo(() => buildSuggestions(session, profile), [session, profile]);
  if (chips.length === 0) return null;
  return (
    <div
      className="px-3 pt-3 pb-1"
      style={{ background: "rgba(245,240,232,0.95)", borderTop: "1px solid rgba(30,74,53,0.04)" }}
    >
      <div className="flex items-center gap-1.5 mb-2" style={{ paddingLeft: 4 }}>
        <Icon name="auto_awesome" size={11} color="var(--mnf-tg)" />
        <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 9 }}>
          {session ? `Tuned for week ${session.mesocycleWeek} \u00b7 ${session.focus}` : "Quick prompts"}
        </span>
      </div>
      <div className="flex gap-2 overflow-x-auto mnf-scroll" style={{ paddingBottom: 4 }}>
        {chips.map((c) => (
          <button
            key={c}
            onClick={() => onPick(c)}
            data-testid={`chip-suggest-${c.slice(0, 20).replace(/\s+/g, "-")}`}
            className="shrink-0 mnf-body hover-elevate active-elevate-2"
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              background: "rgba(30,74,53,0.06)",
              color: "var(--mnf-fd)",
              border: "1px solid rgba(30,74,53,0.10)",
              fontSize: 12,
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div
        className="flex items-center gap-1.5"
        style={{
          padding: "12px 16px", borderRadius: "18px 18px 18px 6px",
          background: "#fff",
          border: "1px solid rgba(30,74,53,0.06)",
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="kiley-typing-dot"
            style={{
              width: 7, height: 7, borderRadius: 999,
              background: "var(--mnf-f)",
              opacity: 0.6,
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
